import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { clearSession, getToken, isTokenExpired } from '@/shared/auth/tokenStorage'
import { refreshAccessToken } from '@/shared/auth/sessionRefresh'
import { API_TIMEOUT_MS, apiBaseUrl } from './baseUrl'

export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: API_TIMEOUT_MS,
})

// Har so'rovga token qo'shiladi
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Backend javobining yagona shakli (`uz.xsib.common.response.XsibResponse`).
 * Sahifalar buni ko'rmaydi — quyidagi interceptor ochib tashlaydi.
 */
export interface XsibEnvelope<T> {
  success: boolean
  requestId: string | null
  timeZone: string | null
  error: { code: number | null; tag: string | null; text: string | null } | null
  data: T
}

function isEnvelope(body: unknown): body is XsibEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'success' in body && 'data' in body
}

/** Envelope ichidagi xato — `translateError` uni `message` orqali o'qiydi. */
export class XsibApiError extends Error {
  constructor(
    message: string,
    readonly tag: string | null,
    readonly code: number | null,
    readonly requestId: string | null,
  ) {
    super(message)
    this.name = 'XsibApiError'
  }
}

/**
 * Envelope BITTA joyda ochiladi.
 *
 * ⚠️ Har chaqiruvda `res.data.data` yozish o'nlab joyda takrorlanardi va
 * bittasini unutish yetarli edi — unutilgani faqat ekranda `undefined`
 * chiqqanda bilinardi. Zod sxemalari TEGILMAYDI: ular ichki (`data`)
 * shaklni tekshiradi va interceptor ularga aynan o'shani beradi.
 */
api.interceptors.response.use((response) => {
  const body: unknown = response.data
  if (isEnvelope(body)) {
    if (!body.success) {
      return Promise.reject(
        new XsibApiError(
          // ⚠️ Qotirilgan o'zbekcha matn TURMASIN — u `translateError` orqali
          // to'g'ridan-to'g'ri ekranga chiqadi. Bo'sh satr `errors.generic` ga tushadi.
          body.error?.text ?? '',
          body.error?.tag ?? null,
          body.error?.code ?? null,
          body.requestId,
        ),
      )
    }
    response.data = body.data
  }
  return response
})

/**
 * Kirish oqimining o'z yo'llari — 401 bu yerda "parol/TOTP noto'g'ri"
 * degani, "sessiya eskirgan" degani EMAS.
 *
 * ⚠️ RO'YXATSIZ NIMA BO'LARDI — bu o'lchangan nuqson (admin paneli,
 * XSB-005, 2026-08-13): TOTP ekranida noto'g'ri kod kiritilganda
 * `/api/partner/totp/verify` 401 qaytaradi, TOTP paytida esa sessiya
 * tokeni hali YO'Q. Quyidagi interceptor sessiyani "tozalab" sahifani
 * `/login` ga TO'LIQ QAYTA YUKLARDI, `TotpPage` ning xato banneri esa
 * chizilib ulgurmasdi — foydalanuvchi nima bo'lganini bilmay loginni
 * qaytadan terardi.
 *
 * ⚠️ Parolda bu SEZILMASDI: u paytda odam allaqachon `/login` da turadi
 * va qayta yo'naltirish ko'zga tashlanmaydi. Nuqson faqat ikkinchi
 * bosqichda ko'rinadi — ya'ni TOTP ekrani yozilishi bilan bu ro'yxat
 * ham SHART bo'lib qoldi.
 *
 * ⚠️ Prefiks (`/api/partner/`) — bitta-bitta sanamaslik uchun: kirish
 * oqimining BARCHA to'rt yo'li shu ostida
 * (`login`, `login/tenant`, `totp/confirm`, `totp/verify`), va beshinchisi
 * qo'shilsa u ham o'zidan qamrab olinadi.
 */
const AUTH_FLOW_PATHS = ['/api/partner/', '/api/auth/refresh', '/api/auth/logout']

function isAuthFlowPath(url: string): boolean {
  return AUTH_FLOW_PATHS.some((path) => url.includes(path))
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  __xsibRetried?: boolean
}

/** 401 sababi SESSIYA ekanini aniqlaydi — `admin` dagi bilan bir xil ikki signal. */
function looksLikeExpiredSession(error: AxiosError): boolean {
  const body = error.response?.data as { error?: { tag?: string | null } | null } | undefined
  if (body?.error?.tag === 'UNAUTHORIZED') return true
  return getToken() === null || isTokenExpired()
}

function forceLogout(): void {
  const onLoginPage = window.location.pathname === '/login'
  clearSession()
  if (!onLoginPage) {
    window.location.replace('/login')
  }
}

/**
 * 401 → AVVAL tokenni yangilash, faqat undan keyin chiqarish.
 *
 * ⚠️ Ilgari bu yerda yangilash YO'Q edi va sababi «`/api/partner/refresh`
 * hali yo'q» deb yozilgandi. Bu noto'g'ri farazga asoslangan: refresh
 * SHERIK uchun ham UMUMIY yo'lda (`POST /api/auth/refresh`) va
 * `RefreshTokenService` har aylanishda sinfni hisobdan qayta o'qib,
 * sherikka statik to'plamini va o'z auditoriyasini qaytadan beradi.
 *
 * ⚠️ Kassir uchun bu admin panelidagidan MUHIMROQ, arzonroq emas: u kun
 * bo'yi ishlaydi va sessiya amal o'rtasida uzilsa — pul olingan, natija
 * noma'lum holatda qoladi.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) return Promise.reject(error)

    const request = error.config as RetriableConfig | undefined
    const retriable =
      request !== undefined &&
      request.__xsibRetried !== true &&
      !isAuthFlowPath(request.url ?? '') &&
      looksLikeExpiredSession(error)

    if (retriable) {
      request.__xsibRetried = true
      const outcome = await refreshAccessToken(getToken())
      if (outcome.status === 'refreshed') {
        request.headers.Authorization = `Bearer ${outcome.token}`
        return api.request(request)
      }
      // 'unavailable' — server javob bermadi, sessiyaga TEGILMAYDI.
      if (outcome.status === 'rejected') forceLogout()
      return Promise.reject(error)
    }

    if (isAuthFlowPath(request?.url ?? '')) return Promise.reject(error)
    if (getToken() === null || isTokenExpired()) forceLogout()
    return Promise.reject(error)
  },
)
